/**
 * 3D virtual keyboard for VR text entry (URLs, or plain text with mode="text"). Big keys for controller rays,
 * hover + press feedback, letters / symbols layers, one-shot shift (press it
 * twice for caps lock) and URL helpers. Unlit materials + troika Text, in the
 * VR menu's palette.
 */
import React, { useMemo, useRef, useState } from 'react';
import { Text } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { vrColors as colors, roundedRect, noRaycast } from './vrUi';

export type KeyAction = { type: 'text'; value: string } | { type: 'backspace' } | { type: 'go' };

interface KeyDef { label: string; value?: string; w?: number; kind?: 'shift' | 'layer' | 'back' | 'go' | 'space' | 'text' }

const LETTERS: KeyDef[][] = [
  '1234567890'.split('').map((c) => ({ label: c })),
  'qwertyuiop'.split('').map((c) => ({ label: c })),
  'asdfghjkl'.split('').map((c) => ({ label: c })),
  [{ label: '⇧', kind: 'shift', w: 1.5 }, ...'zxcvbnm'.split('').map((c) => ({ label: c })), { label: '⌫', kind: 'back', w: 1.5 }],
];
const SYMBOLS: KeyDef[][] = [
  '1234567890'.split('').map((c) => ({ label: c })),
  ['-', '_', '=', '+', '&', '?', '#', '%', '@', ':'].map((c) => ({ label: c })),
  ['~', '!', '$', "'", '(', ')', '*', ',', ';'].map((c) => ({ label: c })),
  [{ label: '[', w: 1.5 }, ...[']', '{', '}', '<', '>', '|', '^'].map((c) => ({ label: c })), { label: '⌫', kind: 'back', w: 1.5 }],
];
const TEXT_BOTTOM = (symbols: boolean, goLabel: string): KeyDef[] => [
  { label: symbols ? 'abc' : '?123', kind: 'layer', w: 1.5 },
  { label: ',', w: 0.9 },
  { label: 'space', kind: 'space', value: ' ', w: 4.2 },
  { label: '.', w: 0.9 },
  { label: '?', w: 0.9 },
  { label: '!', w: 0.9 },
  { label: goLabel, kind: 'go', w: 1.5 },
];
const BOTTOM = (symbols: boolean, goLabel: string): KeyDef[] => [
  { label: symbols ? 'abc' : '?123', kind: 'layer', w: 1.5 },
  { label: 'https://', kind: 'text', value: 'https://', w: 1.7 },
  { label: 'www.', kind: 'text', value: 'www.', w: 1.2 },
  { label: '/', w: 0.9 },
  { label: 'space', kind: 'space', value: ' ', w: 1.6 },
  { label: '.', w: 0.9 },
  { label: '.com', kind: 'text', value: '.com', w: 1.2 },
  { label: goLabel, kind: 'go', w: 1.5 },
];

export const KEY = 0.062;
export const GAP = 0.008;

function Key({ def, x, y, unit, upper, shiftState, onPress }: {
  def: KeyDef; x: number; y: number; unit: number; upper: boolean; shiftState: 0 | 1 | 2; onPress: (def: KeyDef) => void;
}) {
  const [hover, setHover] = useState(false);
  const [down, setDown] = useState(false);
  const group = useRef<THREE.Group>(null);
  const width = (def.w ?? 1) * unit + ((def.w ?? 1) - 1) * GAP;
  const shape = useMemo(() => roundedRect(width, KEY, 0.01), [width]);
  useFrame((_, dt) => {
    if (!group.current) return;
    const s = down ? 0.9 : hover ? 1.05 : 1;
    group.current.scale.x += (s - group.current.scale.x) * Math.min(1, dt * 25);
    group.current.scale.y = group.current.scale.x;
    group.current.position.z = down ? -0.004 : hover ? 0.005 : 0;
  });
  const isGo = def.kind === 'go';
  const active = def.kind === 'shift' && shiftState > 0;
  const base = isGo ? colors.teal : active ? '#124940' : def.kind && def.kind !== 'text' ? '#2a3d50' : colors.card;
  const color = down ? '#00e0cf' : hover ? (isGo ? '#2ee6d6' : colors.hover) : base;
  const label = def.kind ? (def.kind === 'shift' && shiftState === 2 ? '⇪' : def.label) : upper ? def.label.toUpperCase() : def.label;
  return <group position={[x + width / 2, y, 0]}>
    <group ref={group}>
      <mesh name="vr-key" userData={{ key: def.label }}
        onPointerOver={(e) => { e.stopPropagation(); setHover(true); }}
        onPointerOut={() => setHover(false)}
        onClick={(e) => { e.stopPropagation(); setDown(true); setTimeout(() => setDown(false), 130); onPress(def); }}>
        <shapeGeometry args={[shape]} />
        <meshBasicMaterial color={color} toneMapped={false} />
      </mesh>
      <Text raycast={noRaycast} position={[0, 0, 0.003]} fontSize={def.label.length > 2 ? 0.02 : 0.028}
        color={isGo ? '#04211e' : colors.text} anchorX="center" anchorY="middle">{label}</Text>
    </group>
  </group>;
}

export default function VRKeyboard({ onKey, goLabel = 'Go', mode = 'url' }: { onKey: (a: KeyAction) => void; goLabel?: string; mode?: 'url' | 'text' }) {
  const [symbols, setSymbols] = useState(false);
  const [shift, setShift] = useState<0 | 1 | 2>(0); // off / once / caps lock
  const lastShift = useRef(0);
  const rows = [...(symbols ? SYMBOLS : LETTERS), (mode === 'text' ? TEXT_BOTTOM : BOTTOM)(symbols, goLabel)];
  const totalWidth = 10 * KEY + 9 * GAP;

  const press = (def: KeyDef) => {
    if (def.kind === 'shift') {
      const now = performance.now();
      setShift((s) => (s === 1 && now - lastShift.current < 400 ? 2 : s === 0 ? 1 : 0));
      lastShift.current = now;
      return;
    }
    if (def.kind === 'layer') { setSymbols((v) => !v); return; }
    if (def.kind === 'back') { onKey({ type: 'backspace' }); return; }
    if (def.kind === 'go') { onKey({ type: 'go' }); return; }
    const raw = def.value ?? def.label;
    onKey({ type: 'text', value: !def.kind && shift > 0 ? raw.toUpperCase() : raw });
    if (shift === 1 && !def.kind) setShift(0);
  };

  return <group>
    {rows.map((row, r) => {
      // Every row spans the same width as the number row; the bottom row
      // stretches its unit to fill it, shorter letter rows are centred.
      const units = row.reduce((n, k) => n + (k.w ?? 1), 0);
      const isBottom = r === rows.length - 1;
      const extraGaps = row.reduce((n, k) => n + ((k.w ?? 1) - 1), 0) * GAP;
      const unit = isBottom ? (totalWidth - (row.length - 1) * GAP - extraGaps) / units : KEY;
      const widths = row.map((k) => (k.w ?? 1) * unit + ((k.w ?? 1) - 1) * GAP);
      const rowWidth = widths.reduce((a, b) => a + b, 0) + (row.length - 1) * GAP;
      let x = -rowWidth / 2;
      const y = -r * (KEY + GAP);
      return row.map((def, i) => {
        const kx = x;
        x += widths[i] + GAP;
        return <Key key={`${r}-${i}-${def.label}`} def={def} x={kx} y={y} unit={unit} upper={shift > 0} shiftState={shift} onPress={press} />;
      });
    })}
  </group>;
}

/** Height of the whole keyboard (for layout). */
export const KEYBOARD_HEIGHT = 5 * KEY + 4 * GAP;
